// src/pages/costing/PageFG.jsx
import { useState, useMemo } from "react";
import { fmt2, fmt4, spKg, sfgCostPerKg, sfgPackBreakdown, MARGINS, PKG_COLS, SAMPLE_KG } from "../../utils/costingUtils";
import { ModalWrap } from "../../components/ModalWrap";
import { FormulaSlots } from "../../components/FormulaSlots";

export function PageFG({ fgs, rms, sfgs, fgFormulas, sfgFormulas, settings, fgContent, packBreak, onSettingsChange, onPackingPrice, search, setSearch, onAdd, onEdit, onFGClick, onCostCell }) {
  const { customPct, packing, labourPerKg, wastageGlobal = 1 } = settings;
  const [hovRow, setHovRow] = useState(null);
  const [hovCol, setHovCol] = useState(null);

  const filteredFGs = useMemo(() =>
    fgs.filter(fg =>
      fg.id.toLowerCase().includes((search||"").toLowerCase()) ||
      fg.name.toLowerCase().includes((search||"").toLowerCase())
    ), [fgs, search]);

  const filteredSFGs = useMemo(() =>
    sfgs.filter(sfg =>
      sfg.id.toLowerCase().includes((search||"").toLowerCase()) ||
      sfg.name.toLowerCase().includes((search||"").toLowerCase())
    ), [sfgs, search]);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Finished Goods</div>
          <div className="page-subtitle">All prices in AED per kg</div>
        </div>
        <div className="page-header-right">
          <button className="btn-primary" onClick={onAdd}>+ Add FG</button>
        </div>
      </div>
      <div className="settings-bar">
        <span className="gl-label">Labour:</span>
        <input className="form-input input-right" style={{ width:68 }} type="number" step="0.01" defaultValue={fmt2(labourPerKg)}
          onBlur={e => onSettingsChange({...settings, labourPerKg:parseFloat(e.target.value)||0})} />
        <span className="gl-unit">AED/kg</span>
        <span className="gl-sep">|</span>
        {packing.map((p, i) => (
          <span key={p.id} style={{ display:"contents" }}>
            <span className="gl-label">{p.label}:</span>
            <input className="form-input input-right" style={{ width:68 }} type="number" step="0.01" defaultValue={fmt2(p.price)}
              onBlur={e => onPackingPrice(p.id, parseFloat(e.target.value)||0)} />
            <span className="gl-unit">AED</span>
            {i < packing.length - 1 && <span className="gl-sep">|</span>}
          </span>
        ))}
        <span className="gl-sep">|</span>
        <span className="gl-label">Custom %:</span>
        <input className="form-input input-right" style={{ width:56 }} type="number" step="0.5" defaultValue={customPct}
          onBlur={e => onSettingsChange({...settings, customPct:parseFloat(e.target.value)||0})} />
        <span className="gl-unit">%</span>
        <div style={{ marginLeft:"auto", display:"flex", gap:6, alignItems:"center" }}>
          <input className="form-input" style={{ width:200 }} placeholder="Search products…" value={search||""}
            onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="table-wrap" style={{ border:"1px solid #e8e8e8", borderRadius:10 }}>
        <table className="data-table" style={{ minWidth: 200 + packing.length * (MARGINS.length + 2) * 72 }}>
          <thead className="thead-two-row">
            <tr>
              <th style={{ minWidth:100 }} rowSpan={2}>FG Code</th>
              <th style={{ textAlign:"right" }} rowSpan={2}>Content/kg</th>
              {packing.map((p, i) => (
                <th key={p.id} colSpan={MARGINS.length + 2}
                  style={{ textAlign:"center", background:PKG_COLS[i].hdrBg, color:PKG_COLS[i].hdrColor,
                    fontSize:10, fontWeight:700, borderBottom:"1px solid #d0e4f7", padding:"5px 4px" }}>
                  {p.label} — AED / kg
                </th>
              ))}
              <th rowSpan={2}></th>
            </tr>
            <tr className="thead-row2">
              {packing.map((p, ci) => (
                <span key={p.id} style={{ display:"contents" }}>
                  <th style={{ textAlign:"right", fontSize:9 }}>Cost/kg</th>
                  {MARGINS.map(m => <th key={m} style={{ textAlign:"right", fontSize:9 }}>{m}%</th>)}
                  <th style={{ textAlign:"right", fontSize:9, background:PKG_COLS[ci].custBg, color:PKG_COLS[ci].custColor }}>{customPct}%★</th>
                </span>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFGs.map((fg, rowIdx) => {
              const cc     = fgContent(fg.id);
              const isHovR = hovRow === rowIdx;
              return (
                <tr key={fg.id} onMouseEnter={() => setHovRow(rowIdx)} onMouseLeave={() => { setHovRow(null); setHovCol(null); }}>
                  <td style={{ background: isHovR ? "#e8f0fe" : "transparent" }}>
                    <div className="link fw-700" style={{ fontSize:12, color:"#2563eb", display:"inline-block" }}
                      onClick={() => onFGClick(fg)}>{fg.id}</div>
                    <span className="badge badge-green" style={{ marginLeft:6 }}>FG</span>
                  </td>
                  <td style={{ textAlign:"right", fontWeight:600, background: isHovR ? "#e8f0fe" : "transparent" }}>{fmt4(cc)}</td>
                  {packing.map((p, ci) => {
                    const bd         = packBreak(fg, p);
                    const baseOffset = 2 + ci * (MARGINS.length + 2);
                    return (
                      <span key={p.id} style={{ display:"contents" }}>
                        {[null, ...MARGINS, "cust"].map((m, mi) => {
                          const colIdx   = baseOffset + mi;
                          const isCustom  = m === "cust";
                          const isCostCol = m === null;
                          const val = isCustom ? spKg(bd.perKg, customPct) : isCostCol ? bd.perKg : spKg(bd.perKg, m);
                          const isHovC = hovCol === colIdx;
                          const isHov  = isHovR || isHovC;
                          const baseBg = isCustom ? PKG_COLS[ci].custBg : PKG_COLS[ci].colBg;
                          return (
                            <td key={mi} style={{
                              textAlign:"right", fontWeight:isCostCol?700:400, fontSize:isCostCol?12:11,
                              background: isHov ? (isCustom ? "#c7dcf7" : "#dce9fd") : baseBg,
                              color: isCustom ? PKG_COLS[ci].custColor : "inherit",
                              cursor: isCostCol ? "pointer" : "default"
                            }}
                              onMouseEnter={() => setHovCol(colIdx)} onMouseLeave={() => setHovCol(null)}
                              onClick={isCostCol ? () => onCostCell(fg, p) : undefined}>
                              {fmt2(val)}
                            </td>
                          );
                        })}
                      </span>
                    );
                  })}
                  <td style={{ background: isHovR ? "#e8f0fe" : "transparent" }}>
                    <button className="btn-small" onClick={() => onEdit(fg)}>Edit</button>
                  </td>
                </tr>
              );
            })}
            {filteredSFGs.length > 0 && (
              <tr><td colSpan={2 + packing.length * (MARGINS.length + 2) + 1}
                style={{ background:"#f8fafc", borderTop:"2px solid #e2e8f0", padding:"6px 12px",
                  fontSize:11, fontWeight:700, color:"#64748b", letterSpacing:"0.05em" }}>
                SEMI-FINISHED GOODS
              </td></tr>
            )}
            {filteredSFGs.map((sfg, rowIdx) => {
              const cc     = sfgCostPerKg(sfg.id, sfgFormulas, rms);
              const isHovR = hovRow === `sfg-${rowIdx}`;
              return (
                <tr key={sfg.id}
                  onMouseEnter={() => setHovRow(`sfg-${rowIdx}`)}
                  onMouseLeave={() => { setHovRow(null); setHovCol(null); }}>
                  <td style={{ background: isHovR ? "#f5f3ff" : "transparent" }}>
                    <span className="fw-700" style={{ fontSize:12 }}>{sfg.id}</span>
                    <span className="badge badge-purple" style={{ marginLeft:6 }}>SFG</span>
                  </td>
                  <td style={{ textAlign:"right", fontWeight:600, background: isHovR ? "#f5f3ff" : "transparent" }}>{fmt4(cc)}</td>
                  {packing.map((p, ci) => {
                    const bd         = sfgPackBreakdown(sfg.id, p, sfgFormulas, rms, labourPerKg, wastageGlobal);
                    const baseOffset = 2 + ci * (MARGINS.length + 2);
                    return (
                      <span key={p.id} style={{ display:"contents" }}>
                        {[null, ...MARGINS, "cust"].map((m, mi) => {
                          const colIdx   = baseOffset + mi;
                          const isCustom  = m === "cust";
                          const isCostCol = m === null;
                          const pct = isCustom ? customPct : m;
                          const val = isCostCol ? bd.perKg : bd.perKg * (1 + pct / 100);
                          const isHovC = hovCol === colIdx;
                          const bg = isHovR ? (isCustom ? PKG_COLS[ci].custBg : "#f5f3ff")
                                            : isHovC ? "#f0f4ff" : isCustom ? PKG_COLS[ci].custBg : "transparent";
                          return (
                            <td key={mi} style={{ textAlign:"right", background:bg, color:isCustom?PKG_COLS[ci].custColor:undefined }}
                              onMouseEnter={() => setHovCol(colIdx)}>
                              {fmt2(val)}
                            </td>
                          );
                        })}
                      </span>
                    );
                  })}
                  <td style={{ background: isHovR ? "#f5f3ff" : "transparent" }} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function FGFormModal({ fg, fgFormulas, sfgFormulas, rms, sfgs, sfgCost, fgContent, packBreak, settings, onEdit, onClose }) {
  const formula = fgFormulas[fg.id] || [];
  const cc      = fgContent(fg.id);
  return (
    <div className="modal wide">
      <div className="modal-header" style={{ marginBottom:4 }}>
        <div className="modal-title">{fg.id}</div>
        <button className="btn-small" onClick={onClose}>✕</button>
      </div>
      <div style={{ fontSize:13, fontWeight:700, marginBottom:14 }}>{fg.name}</div>
      <div className="table-wrap">
      <table className="data-table" style={{ marginBottom:14 }}>
        <thead><tr>
          <th>Ingredient</th><th>Type</th>
          <th style={{ textAlign:"right" }}>Qty</th>
          <th style={{ textAlign:"right" }}>Cost/kg</th>
          <th style={{ textAlign:"right" }}>Contribution</th>
        </tr></thead>
        <tbody>
          {formula.map(f => {
            const isSFG  = f.type==="SFG" || f.type==="sfg";
            const compId = f.componentId ?? f.id;
            const item   = isSFG ? sfgs.find(s => s.id===compId) : rms.find(r => r.id===compId);
            const cph    = isSFG ? sfgCost(compId) : (item?.price||0);
            const prop   = f.proportion ?? f.qty ?? 0;
            return (
              <tr key={compId}>
                <td className="fw-600">{item ? item.name : compId}</td>
                <td><span className={`badge badge-${isSFG?"purple":"gray"}`}>{isSFG?"SFG":"RM"}</span></td>
                <td style={{ textAlign:"right" }}>{(prop*100).toFixed(2)}%</td>
                <td style={{ textAlign:"right" }}>AED {fmt4(cph)}</td>
                <td className="fw-700" style={{ textAlign:"right" }}>AED {fmt4(cph*prop)}</td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={4} className="fw-700">Content cost / kg</td>
            <td className="fw-700" style={{ textAlign:"right", color:"#166534" }}>AED {fmt4(cc)}</td>
          </tr>
        </tbody>
      </table>
      </div>
      <div className="table-wrap">
      <table className="data-table">
        <thead><tr>
          <th>Pack</th>
          <th style={{ textAlign:"right" }}>Cost/kg</th>
          {MARGINS.map(m => <th key={m} style={{ textAlign:"right" }}>{m}%</th>)}
          <th style={{ textAlign:"right", color:"#2563eb" }}>{settings.customPct}%★</th>
        </tr></thead>
        <tbody>
          {settings.packing.map(p => {
            const bd = packBreak(fg, p);
            return (
              <tr key={p.id}>
                <td className="fw-600">{p.label}</td>
                <td className="fw-700" style={{ textAlign:"right" }}>AED {fmt2(bd.perKg)}</td>
                {MARGINS.map(m => <td key={m} style={{ textAlign:"right" }}>AED {fmt2(spKg(bd.perKg, m))}</td>)}
                <td className="fw-700" style={{ textAlign:"right", color:"#2563eb" }}>AED {fmt2(spKg(bd.perKg, settings.customPct))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="form-footer" style={{ marginTop:16 }}>
        <button className="btn-small" onClick={() => onEdit(fg)}>Edit formula</button>
        <button className="btn" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

export function CostCellModal({ fg, pkg, packBreak, customPct, labourPerKg, wastageGlobal, onClose }) {
  const bd = packBreak(fg, pkg);
  return (
    <div className="modal">
      <div className="modal-header">
        <div className="modal-title">{fg.id} — {pkg.label}</div>
        <button className="btn-small" onClick={onClose}>✕</button>
      </div>
      <div style={{ background:"#f8faff", border:"1px solid #d1e3ff", borderRadius:7, padding:"10px 12px", marginBottom:14 }}>
        {[
          [`Raw material content (${pkg.size} kg × AED ${fmt4(bd.cc)}/kg)`,   `AED ${fmt2(bd.contentCost)}`],
          [`Labour & overhead (${pkg.size} kg × AED ${fmt4(labourPerKg)}/kg)`, `AED ${fmt2(bd.labourCost)}`],
          [`Production cost subtotal`,                                           `AED ${fmt2(bd.prodCost)}`],
          [`Container (${pkg.label})`,                                           `AED ${fmt2(bd.container)}`],
          [`Subtotal`,                                                            `AED ${fmt2(bd.subtotal)}`],
          [`Wastage (${wastageGlobal}%)`,                                         `+ AED ${fmt2(bd.wastage)}`],
          [`Sample (${SAMPLE_KG} kg × AED ${fmt4(bd.cc)}/kg)`,                  `+ AED ${fmt2(bd.sample)}`],
          [`Total pack cost`,                                                     `AED ${fmt2(bd.packTotal)}`],
        ].map(([l,v], i) => (
          <div key={i} className={`bd-row${i===7?" total":""}`}><span>{l}</span><span>{v}</span></div>
        ))}
        <div style={{ marginTop:10, paddingTop:8, borderTop:"1px solid #d1e3ff", display:"flex", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, fontWeight:700, color:"#2563eb" }}>÷ {pkg.size} kg = Cost per kg</span>
          <span style={{ fontSize:16, fontWeight:700, color:"#2563eb" }}>AED {fmt2(bd.perKg)}/kg</span>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${MARGINS.length+1},1fr)`, gap:6 }}>
        {MARGINS.map(m => (
          <div key={m} style={{ background:"#f8faff", borderRadius:7, padding:7, textAlign:"center" }}>
            <div style={{ fontSize:10, color:"#aaa" }}>{m}%</div>
            <div style={{ fontSize:13, fontWeight:700, marginTop:2 }}>AED {fmt2(spKg(bd.perKg, m))}</div>
          </div>
        ))}
        <div style={{ background:"#eef3ff", borderRadius:7, padding:7, textAlign:"center" }}>
          <div style={{ fontSize:10, color:"#2563eb" }}>{customPct}%★</div>
          <div style={{ fontSize:13, fontWeight:700, color:"#2563eb", marginTop:2 }}>AED {fmt2(spKg(bd.perKg, customPct))}</div>
        </div>
      </div>
    </div>
  );
}

export function AddFGModal({ onSave, existingIds, onCancel }) {
  const [name,     setName]     = useState("");
  const [customId, setCustomId] = useState("");
  const [err,      setErr]      = useState("");
  function validate() {
    if (!name.trim())                       { setErr("Product name required."); return; }
    if (!customId.trim())                   { setErr("FG Code required."); return; }
    if (existingIds.includes(customId.trim())) { setErr("FG Code already exists."); return; }
    onSave({ name: name.trim(), customId: customId.trim() });
  }
  return (
    <ModalWrap title="Add Finished Good" onClose={onCancel}>
      <div className="form-row">
        <span className="form-label">FG Code</span>
        <input className="form-input" style={{ flex:1 }} value={customId}
          onChange={e => { setCustomId(e.target.value); setErr(""); }} placeholder="e.g. FG-001" autoFocus />
      </div>
      <div className="form-row">
        <span className="form-label">FG Name</span>
        <input className="form-input" style={{ flex:1 }} value={name} onChange={e => setName(e.target.value)} />
      </div>
      {err && <div className="alert-warn" style={{ marginBottom:8 }}>{err}</div>}
      <div className="form-sep" />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" onClick={validate}>Add</button>
      </div>
    </ModalWrap>
  );
}

export function EditFGModal({ fg, rms, sfgs, formula, onSave, onCancel }) {
  const [slots, setSlots] = useState(formula.map(f => ({
    id:   f.componentId ?? f.id,
    type: (f.type==="SFG" || f.type==="sfg") ? "sfg" : "rm",
    qty:  String(((f.proportion ?? f.qty ?? 0) * 100).toFixed(2)),
  })));
  return (
    <ModalWrap title={`${fg.id} — ${fg.name}`} onClose={onCancel}>
      <div style={{ fontSize:11, color:"#888", marginTop:-10, marginBottom:12 }}>Mix of RMs and SFGs (%). Total must = 100.00%</div>
      <FormulaSlots slots={slots} setSlots={setSlots} rms={rms} sfgs={sfgs} showType />
      <div className="form-footer">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn-primary" onClick={() =>
          onSave(fg.id, slots.filter(s => s.id && parseFloat(s.qty) > 0).map(s => ({
            componentType: s.type==="sfg" ? "SFG" : "RM",
            componentId:   s.id,
            quantity:      parseFloat(s.qty) / 100
          })))}>Save</button>
      </div>
    </ModalWrap>
  );
}
